// orchestrator_fullstack/frontend/src/app/components/DangerConfirmModal.tsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * Reusable confirmation modal for dangerous operations.
 * - requires typing a keyword (DELETE / ROTATE etc.)
 * - supports optional audit reason
 *
 * Backwards-compatible with existing callers:
 *   <DangerConfirmModal
 *     open
 *     title
 *     description?
 *     confirmKeyword="DELETE"   (now optional; defaults to "CONFIRM")
 *     confirmLabel?
 *     requireReason?
 *     onCancel()
 *     onConfirm({reason?})
 *   />
 */
export default function DangerConfirmModal({
  open,
  title,
  description,
  confirmKeyword,
  confirmLabel = "Confirm",
  requireReason = false,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description?: string;

  /**
   * Keyword required to confirm (e.g. DELETE / ROTATE).
   * NOTE: made optional to prevent runtime crashes if a caller forgets it.
   */
  confirmKeyword?: string;

  confirmLabel?: string;
  requireReason?: boolean;

  onCancel: () => void;
  onConfirm: (args: { reason?: string }) => Promise<void> | void;
}) {
  const [typed, setTyped] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);

  // Normalize keyword safely (never call .trim() on undefined)
  const keyword = useMemo(() => {
    const k = String(confirmKeyword ?? "CONFIRM").trim();
    return k.length ? k : "CONFIRM";
  }, [confirmKeyword]);

  // Reset state each time modal is opened (prevents stale input + busy lock)
  useEffect(() => {
    if (!open) return;
    setTyped("");
    setReason("");
    setBusy(false);
  }, [open]);

  const ok = useMemo(() => {
    const kwOk = (typed ?? "").trim().toUpperCase() === keyword.toUpperCase();
    const reasonOk = !requireReason || (reason ?? "").trim().length >= 3;
    return kwOk && reasonOk;
  }, [typed, keyword, requireReason, reason]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
      onMouseDown={(e) => {
        // click outside to cancel (common UX); keep safe and non-breaking
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 560,
          padding: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.20)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>{title}</h3>
        {description ? <p style={{ color: "#555", marginTop: 0 }}>{description}</p> : null}

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
            Type <strong>{keyword}</strong> to continue
          </div>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value ?? "")}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid #ddd",
              padding: 10,
              fontFamily: "monospace",
            }}
            placeholder={keyword}
            autoFocus
            disabled={busy}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
            Audit reason {requireReason ? "(required)" : "(optional)"}
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value ?? "")}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid #ddd",
              padding: 10,
            }}
            placeholder="Why are you doing this?"
            disabled={busy}
          />
          {requireReason && (reason ?? "").trim().length > 0 && (reason ?? "").trim().length < 3 ? (
            <div style={{ marginTop: 6, fontSize: 12, color: "#b00020" }}>
              Please enter at least 3 characters.
            </div>
          ) : null}
        </div>


        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button type="button" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!ok || busy}
            onClick={async () => {
              if (!ok || busy) return;
              setBusy(true);
              try {
                await onConfirm({ reason: (reason ?? "").trim() || undefined });
                onCancel();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}